import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";

async function fetchJson(url: string) {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
  return res.json();
}

export function useFriends() {
  return useQuery({
    queryKey: ["/api/friends"],
    queryFn: () => fetchJson("/api/friends"),
  });
}

export function useFriendRequests() {
  return useQuery({
    queryKey: ["/api/friends/requests"],
    queryFn: () => fetchJson("/api/friends/requests"),
    staleTime: 0,
    refetchOnMount: "always",
    refetchInterval: 30_000,
  });
}

export function useSendFriendRequest() {
  return useMutation({
    mutationFn: (username: string) =>
      apiRequest("POST", "/api/friends/request", { username }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/friends"] });
      queryClient.invalidateQueries({ queryKey: ["/api/friends/requests"] });
    },
  });
}

export function useAcceptRequest() {
  return useMutation({
    mutationFn: (requestId: number) =>
      apiRequest("POST", `/api/friends/requests/${requestId}/accept`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/friends"] });
      queryClient.invalidateQueries({ queryKey: ["/api/friends/requests"] });
    },
  });
}

export function useRejectRequest() {
  return useMutation({
    mutationFn: (requestId: number) =>
      apiRequest("POST", `/api/friends/requests/${requestId}/reject`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/friends/requests"] });
    },
  });
}

export function useRemoveFriend() {
  return useMutation({
    mutationFn: (friendId: number) =>
      apiRequest("DELETE", `/api/friends/${friendId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/friends"] });
    },
  });
}

export function useFriendVibes(friendId: number) {
  return useQuery({
    queryKey: ["/api/friends", friendId, "vibes"],
    queryFn: () => fetchJson(`/api/friends/${friendId}/vibes`),
    enabled: !!friendId,
  });
}

export function useUpdateLocationSharing() {
  return useMutation({
    mutationFn: ({ friendId, enabled }: { friendId: number; enabled: boolean }) =>
      apiRequest("PUT", `/api/friends/${friendId}/location-sharing`, { enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/friends"] });
    },
  });
}

export function useFriendMapPins() {
  return useQuery({
    queryKey: ["/api/friends/map-pins"],
    queryFn: () => fetchJson("/api/friends/map-pins"),
    refetchInterval: 60000,
  });
}
